"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listSaves, type SaveSlotId, type SaveSummary } from "@/engine/save-system";

// ---------- master palette (PIXEL_STYLE.md) ----------
const CREAM = "#f5ecd6";
const SAND = "#b89968";
const BRONZE = "#8a7a5c";
const WALNUT = "#5c4a30";
const MINT = "#bcd4a0";
const MOSS = "#7a9a4a";
const FOREST = "#5c7935";
const DEEP_FOREST = "#3d5224";
const PALE_SKY = "#cfe7f5";
const SHALLOW = "#a8d2e8";
const OCEAN = "#6a9bc4";
const DEEP = "#3d6890";
const INK = "#1a1a1a";
const GOLD = "#c5a83a";

// ---------- types ----------
type Props = {
  onNewGame: () => void;
  onContinue: (slotId: SaveSlotId) => void;
  onSettings: () => void;
};

type RootItem = "new" | "continue" | "settings" | "credits";

const ROOT_ITEMS: { id: RootItem; label: string }[] = [
  { id: "new", label: "New Game" },
  { id: "continue", label: "Continue" },
  { id: "settings", label: "Settings" },
  { id: "credits", label: "Credits" },
];

const SLOT_ORDER: SaveSlotId[] = ["auto", "manual1", "manual2", "manual3"];
const SLOT_LABELS: Record<SaveSlotId, string> = {
  auto: "Auto",
  manual1: "Slot 1",
  manual2: "Slot 2",
  manual3: "Slot 3",
};

// ---------- audio (best-effort, silent if missing) ----------
function clickSfx() {
  if (typeof window === "undefined") return;
  const audio = (window as unknown as {
    surggiesAudio?: { playClickSfx?: () => void };
  }).surggiesAudio;
  try {
    audio?.playClickSfx?.();
  } catch {
    /* silent */
  }
}

function isAudioInitialized(): boolean {
  if (typeof window === "undefined") return false;
  const audio = (window as unknown as {
    surggiesAudio?: { initialized?: boolean };
  }).surggiesAudio;
  return !!(audio?.initialized);
}

// ---------- helpers ----------
function formatPlayTime(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m}m play`;
  return `${h}h ${m}m play`;
}

function summarizeSlot(s: SaveSummary): string {
  const partySize = s.partyLevels.length;
  const top = s.partyLevels.length > 0 ? Math.max(...s.partyLevels) : 0;
  return `Lv${top} party (${partySize}), ${formatPlayTime(s.playTimeMs)}`;
}

// ---------- component ----------
export function TitleScreen({ onNewGame, onContinue, onSettings }: Props) {
  const [mode, setMode] = useState<"root" | "continue" | "credits">("root");
  const [rootCursor, setRootCursor] = useState(0);
  const [slotCursor, setSlotCursor] = useState(0);
  const [saves, setSaves] = useState<Array<SaveSummary | null>>(() => [
    null,
    null,
    null,
    null,
  ]);
  const [audioHintVisible, setAudioHintVisible] = useState(true);
  const audioHintRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Read saves on mount (client-only safe).
    setSaves(listSaves());
  }, []);

  // Poll for audio initialization to hide the hint
  useEffect(() => {
    if (!audioHintVisible) return;
    audioHintRef.current = setInterval(() => {
      if (isAudioInitialized()) {
        setAudioHintVisible(false);
        if (audioHintRef.current !== null) {
          clearInterval(audioHintRef.current);
          audioHintRef.current = null;
        }
      }
    }, 1000);
    return () => {
      if (audioHintRef.current !== null) {
        clearInterval(audioHintRef.current);
        audioHintRef.current = null;
      }
    };
  }, [audioHintVisible]);

  const anySave = useMemo(() => saves.some((s) => s !== null), [saves]);

  const confirmRoot = useCallback((index?: number) => {
    // Explicit index for mouse clicks — closure cursor is stale when
    // setRootCursor(i) and confirmRoot() fire in the same click handler.
    const item = ROOT_ITEMS[index ?? rootCursor];
    clickSfx();
    switch (item.id) {
      case "new":
        onNewGame();
        return;
      case "continue":
        setMode("continue");
        setSlotCursor(0);
        return;
      case "settings":
        onSettings();
        return;
      case "credits":
        setMode("credits");
        return;
    }
  }, [rootCursor, onNewGame, onSettings]);

  const confirmSlot = useCallback((index?: number) => {
    const slot = SLOT_ORDER[index ?? slotCursor];
    if (!saves[index ?? slotCursor]) return; // empty slot — no-op
    clickSfx();
    onContinue(slot);
  }, [slotCursor, saves, onContinue]);

  // keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;
      const nav =
        k === "ArrowUp" ||
        k === "ArrowDown" ||
        k === "Enter" ||
        k === "z" ||
        k === "Z" ||
        k === "x" ||
        k === "X" ||
        k === "Escape";
      if (nav) e.preventDefault();

      if (mode === "root") {
        if (k === "ArrowUp")
          setRootCursor((c) => (c - 1 + ROOT_ITEMS.length) % ROOT_ITEMS.length);
        else if (k === "ArrowDown")
          setRootCursor((c) => (c + 1) % ROOT_ITEMS.length);
        else if (k === "Enter" || k === "z" || k === "Z") confirmRoot();
        return;
      }

      if (mode === "continue") {
        if (k === "ArrowUp")
          setSlotCursor((c) => (c - 1 + SLOT_ORDER.length) % SLOT_ORDER.length);
        else if (k === "ArrowDown")
          setSlotCursor((c) => (c + 1) % SLOT_ORDER.length);
        else if (k === "Enter" || k === "z" || k === "Z") confirmSlot();
        else if (k === "x" || k === "X" || k === "Escape") {
          clickSfx();
          setMode("root");
        }
        return;
      }

      if (mode === "credits") {
        if (k === "x" || k === "X" || k === "Escape" || k === "Enter") {
          clickSfx();
          setMode("root");
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, confirmRoot, confirmSlot]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        background: PALE_SKY,
        fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
        color: INK,
        imageRendering: "pixelated",
      }}
    >
      {/* ---------- BACKDROP: parallax layers ---------- */}
      <Backdrop />

      {/* ---------- WALKING SPRITE ---------- */}
      <img
        src="/assets/surggies/char_surggie_1.png"
        alt=""
        aria-hidden
        style={{
          position: "absolute",
          bottom: "calc(40% + 2px)",
          left: 0,
          width: 32,
          height: "auto",
          imageRendering: "pixelated",
          pointerEvents: "none",
          zIndex: 10,
          animation: "surggies-sprite-walk 8s linear infinite",
        }}
      />

      {/* ---------- TITLE ---------- */}
      <div
        style={{
          position: "absolute",
          top: "8%",
          left: 0,
          right: 0,
          textAlign: "center",
          pointerEvents: "none",
          animation: "surggies-title-bob 2.5s ease-in-out infinite",
        }}
      >
        <PixelTitle text="SURGGIES" />
        <div
          style={{
            marginTop: "0.75rem",
            fontStyle: "italic",
            fontWeight: 700,
            fontSize: "clamp(0.85rem, 1.6vw, 1.15rem)",
            letterSpacing: "0.06em",
            textShadow: `1px 1px 0 ${CREAM}`,
            animation: "surggies-subtitle-pulse 2s ease-in-out infinite",
            color: INK,
          }}
        >
          Catch them all. Build your empire.
        </div>
      </div>

      {/* ---------- MENU PANEL (bottom 40%) ---------- */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "40%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "1.25rem 1rem 1.5rem",
        }}
      >
        <div
          role="dialog"
          aria-label="Title menu"
          style={{
            width: "min(560px, 100%)",
            backgroundColor: CREAM,
            border: `2px solid ${INK}`,
            boxShadow: `inset 1px 1px 0 0 ${CREAM}, inset -1px -1px 0 0 ${WALNUT}`,
            padding: "1rem 1.1rem",
            backgroundImage:
              // tiny static noise texture (no soft alpha, just sparse pixel grain)
              `repeating-linear-gradient(0deg, transparent 0 3px, rgba(92,74,48,0.06) 3px 4px)`,
          }}
        >
          {mode === "root" && (
            <ul
              role="menu"
              aria-label="Main menu"
              style={{ listStyle: "none", margin: 0, padding: 0 }}
            >
              {ROOT_ITEMS.map((it, i) => {
                const active = i === rootCursor;
                const isContinueDisabled = it.id === "continue" && !anySave;
                return (
                  <li key={it.id} style={{ margin: "0.25rem 0" }}>
                    <MenuButton
                      label={it.label}
                      active={active}
                      dimmed={isContinueDisabled}
                      onMouseEnter={() => setRootCursor(i)}
                      onClick={() => {
                        setRootCursor(i);
                        confirmRoot(i);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {mode === "continue" && (
            <div>
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: "0.85rem",
                  marginBottom: "0.5rem",
                  color: INK,
                }}
              >
                Continue —
                <span
                  style={{
                    marginLeft: "0.5rem",
                    color: WALNUT,
                    fontSize: "0.75rem",
                  }}
                >
                  X / Esc back
                </span>
              </div>

              {!anySave ? (
                <div
                  style={{
                    padding: "1rem",
                    border: `2px solid ${INK}`,
                    backgroundColor: SAND,
                    color: INK,
                    textAlign: "center",
                    fontWeight: 700,
                  }}
                >
                  No saves found
                </div>
              ) : (
                <ul
                  role="menu"
                  aria-label="Save slots"
                  style={{ listStyle: "none", margin: 0, padding: 0 }}
                >
                  {SLOT_ORDER.map((slot, i) => {
                    const data = saves[i];
                    const active = i === slotCursor;
                    const empty = !data;
                    const subline = empty
                      ? "(empty)"
                      : summarizeSlot(data as SaveSummary);
                    return (
                      <li key={slot} style={{ margin: "0.25rem 0" }}>
                        <MenuButton
                          label={`${SLOT_LABELS[slot]}: ${subline}`}
                          active={active}
                          dimmed={empty}
                          onMouseEnter={() => setSlotCursor(i)}
                          onClick={() => {
                            setSlotCursor(i);
                            if (!empty) confirmSlot(i);
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {mode === "credits" && (
            <div>
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: "0.85rem",
                  marginBottom: "0.5rem",
                }}
              >
                Credits —
                <span
                  style={{
                    marginLeft: "0.5rem",
                    color: WALNUT,
                    fontSize: "0.75rem",
                  }}
                >
                  Z / Enter / Esc back
                </span>
              </div>
              <p style={{ margin: 0, lineHeight: 1.5, fontSize: "0.95rem" }}>
                Built with care on a muted coastal palette.
                <br />
                Art derived from Tuxemon (CC-BY-SA) and Kenney CC0 packs.
                <br />
                See public/assets/surggies/CREDITS.md.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ---------- AUDIO HINT ---------- */}
      {audioHintVisible && (
        <div
          aria-live="polite"
          style={{
            position: "absolute",
            bottom: "0.75rem",
            left: 0,
            right: 0,
            textAlign: "center",
            color: "#888888",
            fontSize: "0.75rem",
            letterSpacing: "0.06em",
            pointerEvents: "none",
            fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
          }}
        >
          Press any key to start audio
        </div>
      )}

      <style>{`
        @keyframes surggies-title-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes surggies-title-shimmer {
          0%, 100% { color: #b89968; }
          50%       { color: #f5ecd6; }
        }
        @keyframes surggies-subtitle-pulse {
          0%, 100% { opacity: 0.7; }
          50%       { opacity: 1.0; }
        }
        @keyframes surggies-sprite-walk {
          0%   { transform: translateX(-50px); }
          100% { transform: translateX(calc(100vw + 50px)); }
        }
        @keyframes surggies-palm-drift-a {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-24px); }
        }
        @keyframes surggies-palm-drift-b {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-14px); }
        }
        .surggies-menu-btn {
          position: relative;
          width: 100%;
          text-align: left;
          font-family: var(--font-geist-mono), ui-monospace, monospace;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-size: 0.95rem;
          line-height: 1.1;
          border: 2px solid #1a1a1a;
          cursor: pointer;
          image-rendering: pixelated;
          transition: padding-left 0.1s ease, background-color 0.1s ease, color 0.1s ease;
        }
        .surggies-menu-btn:not(.surggies-menu-btn--active):not(.surggies-menu-btn--dimmed):hover,
        .surggies-menu-btn:not(.surggies-menu-btn--active):focus-visible {
          padding-left: 2.25rem !important;
        }
        .surggies-menu-btn--cursor-indicator {
          position: absolute;
          left: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.95rem;
          line-height: 1;
        }
      `}</style>
    </div>
  );
}

// ---------- title text (gold shimmer + 2px ink outline) ----------
function PixelTitle({ text }: { text: string }) {
  // 8-direction 2px outline via layered text-shadow (hard pixels, no blur).
  const outline = [
    "-2px -2px 0",
    "0 -2px 0",
    "2px -2px 0",
    "2px 0 0",
    "2px 2px 0",
    "0 2px 0",
    "-2px 2px 0",
    "-2px 0 0",
    "-2px -1px 0",
    "2px -1px 0",
    "-1px -2px 0",
    "1px -2px 0",
    "1px 2px 0",
    "-1px 2px 0",
    "2px 1px 0",
    "-2px 1px 0",
  ]
    .map((o) => `${o} ${INK}`)
    .join(", ");

  return (
    <h1
      style={{
        margin: 0,
        fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
        fontWeight: 900,
        fontSize: "clamp(3rem, 9vw, 6.5rem)",
        letterSpacing: "0.08em",
        textShadow: outline,
        textTransform: "uppercase",
        lineHeight: 1,
        imageRendering: "pixelated",
        color: GOLD,
        animation: "surggies-title-shimmer 3s linear infinite",
      }}
    >
      {text}
    </h1>
  );
}

// ---------- menu button ----------
function MenuButton({
  label,
  active,
  dimmed,
  onMouseEnter,
  onClick,
}: {
  label: string;
  active: boolean;
  dimmed?: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const classNames = [
    "surggies-menu-btn",
    active ? "surggies-menu-btn--active" : "",
    dimmed ? "surggies-menu-btn--dimmed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      role="menuitem"
      className={classNames}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        padding: "0.55rem 0.75rem 0.55rem 1.75rem",
        backgroundColor: active ? INK : CREAM,
        color: active ? CREAM : INK,
        opacity: dimmed ? 0.55 : 1,
      }}
    >
      {active && (
        <span
          aria-hidden
          className="surggies-menu-btn--cursor-indicator"
          style={{ color: CREAM }}
        >
          &#9654;
        </span>
      )}
      {label}
    </button>
  );
}

// ---------- backdrop: parallax sky / mountains / boardwalk / palms ----------
function Backdrop() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        imageRendering: "pixelated",
      }}
    >
      {/* sky gradient — stepped, not smooth (3 bands) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(
            ${PALE_SKY} 0%,
            ${PALE_SKY} 40%,
            ${SHALLOW} 40%,
            ${SHALLOW} 70%,
            ${SHALLOW} 100%
          )`,
          willChange: "transform",
          // Sky is slowest — drifts very gently via palm-drift-b at slow speed
        }}
      />

      {/* distant mountains silhouette (back parallax — slow) */}
      <div
        style={{
          position: "absolute",
          left: "-5%",
          right: "-5%",
          bottom: "45%",
          height: "22%",
          backgroundColor: OCEAN,
          clipPath:
            "polygon(0% 100%, 0% 60%, 8% 35%, 14% 55%, 22% 25%, 30% 50%, 38% 30%, 46% 55%, 56% 20%, 64% 45%, 72% 30%, 80% 55%, 88% 35%, 100% 60%, 100% 100%)",
          opacity: 1,
          willChange: "transform",
          animation: "surggies-palm-drift-b 60s ease-in-out infinite alternate",
        }}
      />
      {/* secondary darker ridge for depth */}
      <div
        style={{
          position: "absolute",
          left: "-5%",
          right: "-5%",
          bottom: "42%",
          height: "14%",
          backgroundColor: DEEP,
          clipPath:
            "polygon(0% 100%, 0% 70%, 10% 50%, 20% 70%, 28% 40%, 38% 65%, 48% 45%, 58% 70%, 68% 40%, 78% 65%, 88% 50%, 100% 70%, 100% 100%)",
          opacity: 0.6,
          willChange: "transform",
          animation: "surggies-palm-drift-b 45s ease-in-out infinite alternate",
        }}
      />

      {/* ocean strip (right side, behind boardwalk band) */}
      <div
        style={{
          position: "absolute",
          left: "60%",
          right: 0,
          bottom: "40%",
          height: "6%",
          backgroundColor: DEEP,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: "60%",
          right: 0,
          bottom: "40%",
          height: "2%",
          backgroundColor: OCEAN,
        }}
      />

      {/* boardwalk horizon — grass band */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "40%",
          height: "8%",
          backgroundColor: MOSS,
          borderTop: `2px solid ${FOREST}`,
          willChange: "transform",
          animation: "surggies-palm-drift-a 20s ease-in-out infinite alternate",
        }}
      />
      {/* grass darker stripes for texture (hard pixel bands) */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "40%",
          height: "8%",
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0 14px, ${FOREST} 14px 16px)`,
          opacity: 0.45,
          willChange: "transform",
          animation: "surggies-palm-drift-a 20s ease-in-out infinite alternate",
        }}
      />

      {/* boardwalk planks under the grass band (fastest parallax layer) */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: "40%",
          bottom: "36%",
          height: "5%",
          backgroundColor: SAND,
          borderTop: `2px solid ${WALNUT}`,
          borderBottom: `2px solid ${WALNUT}`,
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0 22px, ${BRONZE} 22px 24px)`,
          willChange: "transform",
          animation: "surggies-palm-drift-a 14s ease-in-out infinite alternate",
        }}
      />

      {/* foreground palm — far-left, slow drift */}
      <PalmSilhouette
        style={{
          left: "-2%",
          bottom: "40%",
          height: "32%",
          willChange: "transform",
          animation: "surggies-palm-drift-a 30s ease-in-out infinite alternate",
        }}
      />
      {/* foreground palm — right, faster drift */}
      <PalmSilhouette
        style={{
          right: "-1%",
          bottom: "40%",
          height: "28%",
          willChange: "transform",
          animation: "surggies-palm-drift-b 22s ease-in-out infinite alternate",
        }}
        flip
      />
      {/* mid-ground small palm */}
      <PalmSilhouette
        style={{
          left: "18%",
          bottom: "40%",
          height: "18%",
          opacity: 0.85,
          willChange: "transform",
          animation: "surggies-palm-drift-b 36s ease-in-out infinite alternate",
        }}
      />
    </div>
  );
}

// ---------- palm silhouette (pure pixel-y shape via clip-path) ----------
function PalmSilhouette({
  style,
  flip,
}: {
  style: React.CSSProperties;
  flip?: boolean;
}) {
  return (
    <div
      style={{
        position: "absolute",
        width: "auto",
        ...style,
        transform: flip ? "scaleX(-1)" : undefined,
        pointerEvents: "none",
      }}
    >
      {/* trunk */}
      <div
        style={{
          position: "absolute",
          left: "46%",
          bottom: 0,
          width: "8%",
          minWidth: "6px",
          height: "75%",
          backgroundColor: WALNUT,
          borderLeft: `2px solid ${INK}`,
          borderRight: `2px solid ${INK}`,
        }}
      />
      {/* trunk segments */}
      <div
        style={{
          position: "absolute",
          left: "46%",
          bottom: 0,
          width: "8%",
          minWidth: "6px",
          height: "75%",
          backgroundImage: `repeating-linear-gradient(0deg, transparent 0 10px, ${INK} 10px 11px)`,
          opacity: 0.5,
        }}
      />
      {/* fronds — 5 ink-outlined moss shapes radiating from the crown */}
      <Frond rotate={-70} />
      <Frond rotate={-35} />
      <Frond rotate={0} />
      <Frond rotate={35} />
      <Frond rotate={70} />
    </div>
  );
}

function Frond({ rotate }: { rotate: number }) {
  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: "8%",
        width: "70%",
        height: "18%",
        transformOrigin: "0% 50%",
        transform: `rotate(${rotate}deg)`,
        backgroundColor: FOREST,
        border: `2px solid ${INK}`,
        borderRadius: 0,
        clipPath:
          "polygon(0% 50%, 12% 20%, 35% 35%, 55% 18%, 78% 38%, 100% 22%, 100% 78%, 78% 62%, 55% 82%, 35% 65%, 12% 80%)",
      }}
    />
  );
}

export default TitleScreen;

// Suppress "unused" warnings for palette constants that are part of the
// canonical palette but not yet referenced inside this file. Keeping them
// imported-as-locals keeps the palette block intact for designers.
void MINT;
void DEEP_FOREST;
